import React, { useMemo } from 'react';
import { Card, Table, Tag, Empty, Skeleton, Row, Col, Statistic, Button, Dropdown } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useResponsive } from '../../../hooks/useResponsive';
import { useCurrency } from '../../../hooks/useCurrency';
import { useAppSelector } from '../../../hooks/redux';
import { useGetCashRegisterHistoryQuery } from '../../../services/cashRegisterApi';
import { exportCSV, exportExcel, exportPDF, getPeriodLabel, ExportConfig } from '../../../utils/reportExport';
import { useRestaurantTimeZone } from '../../../hooks/useTimeZone';
import type { CashRegisterDto } from '../../../types/cashRegister';

interface ShiftSummaryProps {
  restaurantId: string;
  fromDate: string;
  toDate: string;
  selectedPeriod?: string;
  customDateRange?: [any, any] | null;
}

const ShiftSummary: React.FC<ShiftSummaryProps> = ({ restaurantId, fromDate, toDate, selectedPeriod, customDateRange }) => {
  const { isMobile } = useResponsive();
  const { format, currencySymbol } = useCurrency();
  const { currentRestaurant } = useAppSelector((state) => state.restaurant);
  const { formatInTimeZone } = useRestaurantTimeZone();

  const { data: shifts, isLoading } = useGetCashRegisterHistoryQuery({
    query: {
      fromDate,
      toDate,
    },
  });

  const summary = useMemo(() => {
    if (!shifts || shifts.length === 0) {
      return { totalShifts: 0, totalInitial: 0, totalFinal: 0, totalVariance: 0 };
    }

    return shifts.reduce(
      (acc, shift) => ({
        totalShifts: acc.totalShifts + 1,
        totalInitial: acc.totalInitial + (shift.initialAmount || 0),
        totalFinal: acc.totalFinal + (shift.finalAmount || 0),
        totalVariance: acc.totalVariance + (shift.variance || 0),
      }),
      { totalShifts: 0, totalInitial: 0, totalFinal: 0, totalVariance: 0 }
    );
  }, [shifts]);

  const columns: ColumnsType<CashRegisterDto> = [
    {
      title: 'Cajero',
      dataIndex: 'userName',
      key: 'userName',
    },
    {
      title: 'Apertura',
      dataIndex: 'openTime',
      key: 'openTime',
      render: (value: string) => formatInTimeZone(value, 'DD/MM/YYYY HH:mm'),
    },
    {
      title: 'Cierre',
      dataIndex: 'closeTime',
      key: 'closeTime',
      render: (value: string | undefined) =>
        value ? formatInTimeZone(value, 'DD/MM/YYYY HH:mm') : <Tag color="blue">Abierta</Tag>,
    },
    {
      title: 'Monto Inicial',
      dataIndex: 'initialAmount',
      key: 'initialAmount',
      render: (value: number) => format(value),
      align: 'right',
    },
    {
      title: 'Monto Final',
      dataIndex: 'finalAmount',
      key: 'finalAmount',
      render: (value: number | undefined) => (value != null ? format(value) : '-'),
      align: 'right',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      render: (status: string) => {
        const color = status === 'CLOSED' ? 'green' : 'yellow';
        const label = status === 'CLOSED' ? 'Closed' : status === 'OPEN' ? 'Open' : 'PendingReview';
        return <Tag color={color}>{label}</Tag>;
      },
    },
  ];

  const handleExportGrid = (fmt: 'csv' | 'excel' | 'pdf') => {
    if (!shifts || shifts.length === 0) return;
    const periodLabel = getPeriodLabel(selectedPeriod || 'today', customDateRange);
    const config: ExportConfig = {
      reportTitle: 'Resumen de Turnos de Caja',
      periodLabel,
      restaurantName: currentRestaurant?.name || 'N/A',
      timestamp: formatInTimeZone(new Date(), 'DD/MM/YYYY HH:mm'),
      sections: [{
        title: 'Turnos',
        titleStyle: 'cash',
        headers: ['Cajero', 'Apertura', 'Cierre', 'Monto Inicial', 'Monto Final', 'Status'],
        rows: shifts.map(s => [
          s.userName || s.userId || 'N/A',
          formatInTimeZone(s.openTime, 'DD/MM/YYYY HH:mm'),
          s.closeTime ? formatInTimeZone(s.closeTime, 'DD/MM/YYYY HH:mm') : 'Abierta',
          `${currencySymbol} ${(s.initialAmount || 0).toFixed(2)}`,
          s.finalAmount != null ? `${currencySymbol} ${s.finalAmount.toFixed(2)}` : '-',
          s.status,
        ]),
      }],
    };
    if (fmt === 'csv') exportCSV(config, 'turnos-caja');
    else if (fmt === 'excel') exportExcel(config, 'turnos-caja');
    else exportPDF(config);
  };

  if (isLoading) {
    return <Skeleton active paragraph={{ rows: 6 }} />;
  }

  if (!shifts || shifts.length === 0) {
    return <Empty description="No hay turnos en el rango seleccionado" />;
  }

  return (
    <div>
      <Row gutter={[16, 16]} style={{ marginBottom: 24 }}>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic title="Total Turnos" value={summary.totalShifts} />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Monto Inicial Total"
              value={summary.totalInitial}
              formatter={(val) => format(val as number)}
              valueStyle={{ color: 'var(--color-success-text)' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Monto Final Total"
              value={summary.totalFinal}
              formatter={(val) => format(val as number)}
              valueStyle={{ color: '#1890ff' }}
            />
          </Card>
        </Col>
        <Col xs={24} sm={12} lg={6}>
          <Card>
            <Statistic
              title="Varianza Total"
              value={summary.totalVariance}
              formatter={(val) => format(val as number)}
              valueStyle={{ color: summary.totalVariance < 0 ? 'var(--color-error-text)' : 'var(--color-success-text)' }}
            />
          </Card>
        </Col>
      </Row>

      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 12 }}>
        <Dropdown menu={{ items: [
          { key: 'csv', label: 'CSV', onClick: () => handleExportGrid('csv') },
          { key: 'excel', label: 'Excel', onClick: () => handleExportGrid('excel') },
          { key: 'pdf', label: 'PDF', onClick: () => handleExportGrid('pdf') },
        ]}}>
          <Button icon={<DownloadOutlined />} size="small">Exportar este grid</Button>
        </Dropdown>
      </div>

      <Table
        dataSource={shifts}
        columns={columns}
        rowKey="id"
        pagination={false}
        scroll={isMobile ? { x: 800 } : undefined}
      />
    </div>
  );
};

export default ShiftSummary;
